import { createContext, useContext, useEffect, useState } from 'react';
import { useIsFetching, useIsMutating, useMutation, useQuery } from 'react-query';
import { getLockedFeatures, getWebSites, getWebSite } from '../services/websitesService';
import { getProfile } from '../services/userService';
import { useHistory, useLocation } from 'react-router';
import RouteConstant from '../routes/routeConstant';
import HamburgerMenuConstant from '../constants/HamburgerMenuConstants';
import { getCardDetails } from '../services/stripe';
import { getSubscriptionById } from '../services/shopifySubscription';
import moment from 'moment';
import { toast } from 'react-toastify';


const AppContext = createContext({});


const AppContextProvider = ({children}) => {
    
    const isFetching = useIsFetching();

    const isFetchingGrowth  = useIsFetching({queryKey: 'fetchSubsGrowth'}); 

    const isMutating = useIsMutating();
    
    const {pathname} = useLocation();

    const history = useHistory();

    const token = localStorage.getItem('token');
    const user_id = localStorage.getItem('user_id');
    
    const isLoggedIn = !!token && !!user_id;

    const [websites, setWebsites] = useState([]);
    const [selectedWebsite, setSelectedWebsite] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [isMenuOpen, setMenuOpen] = useState(false);
    const [planDetails, setPlanDetails] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isMenuCollapsed, setMenuCollapsed] = useState(false);
    const [isBgDisabled, setIsBgDisabled] = useState(false); 
    const [discardCallBack, setDiscardCallBack] = useState(null);
    const [ isDiscardPromptOpen, setDiscardPromptOpen ] = useState(false);
    const [ pathToNavigate, setPathToNavigate ] = useState(null);
    const [ isPricingPopupOpen, setPricingPopupOpen ] = useState(false);
    const [ isChangePlanPopupOpen, setChangePlanPopupOpen ] = useState(false);
    const [ isCancelPlanPopupOpen, setCancelPlanPopupOpen ] = useState(false);
    const [ chosenPlan, setChosenPlan] = useState(false);
    const [ availableFeatures, setAvailableFeatures ] = useState([]);
    const [ isVerifyPopupOpen, setIsVerifyPopupOpen ] = useState(false);
    const [ isPaymentSuccess, setPaymentSuccess] = useState(false);
    const [ isUpdatePlanSuccess, setUpdatePlanSuccess] = useState(false);
    const [ isCancelPlanSuccess, setCancelPlanSuccess] = useState(false);
    const [ isCardSuccess, setCardSuccess] = useState(false);
    const [ isPaymentFailure, setPaymentFailure] = useState(false);
    const [ isCardFailure, setCardFailure] = useState(false);
    const [ isUpdatePlanFailure, setUpdatePlanFailure] = useState(false);
    const [ isCancelPlanFailure, setCancelPlanFailure] = useState(false);
    const [ cardDetails, setCardDetails ] = useState(null);
    const [ chatVisibility, setChatVisibility ] = useState('minimized');
    const [ isShopify, setIsShopify ] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isPromotionalUser, setIsPromotionalUser] = useState(true);
    const [isShopifyFreeUser, setShopifyFreeUser] = useState(false);

    const [defaultWebsite, setDefaultWebsite] = useState(null);

    useEffect(() => {
        const url = new URL(window.location.href);
        const isSuccess = url.searchParams.get('payment_success');
        if(isSuccess === 'true') {
            setPaymentSuccess(true);
        }
        if(isSuccess === 'false') {
            setPaymentFailure(true);
        }

        const cardUpdate = url.searchParams.get('card_update');
        if(cardUpdate === 'true') {
            setCardSuccess(true);
        }
        if(cardUpdate === 'false') {
            setCardFailure(true);
        }
    },[])

    useEffect(() => {
        if(selectedWebsite) {
            const routeIndex = RouteConstant.findIndex(({path, role, userType }) => {
                return path === pathname && role.includes(selectedWebsite.role) && userType.includes(selectedWebsite.user_type);
            });

            if(routeIndex === -1) {
                history.push('/dashboard');
            }
        }
    }, [selectedWebsite]);

    const getCurrentPathAccess = () => {
        const access = {
            userRoles: null,
            userTypes: null,
        };    
        HamburgerMenuConstant.forEach((menu) => {
            if(pathname.includes(menu.path)) {
                access.userRoles = menu.roles;
                access.userTypes = menu.useType;
            }
            if(menu.subMenu) {
                if(pathname.includes(menu.subMenu.path)) {
                    access.userRoles = menu.subMenu.roles;
                    access.userTypes = menu.subMenu.useType;
                }
            }
        });
        return access;
    }

    const addWebsite = (website) => {
        const newWebsites = [...websites, website];
        setWebsites(newWebsites);
    }

    const updateWebsites = (website) => {
        const updatedWebsites = websites.map((existingWebsite) => existingWebsite.id === website.id ? website : existingWebsite);
        setWebsites(updatedWebsites);
        const {userRoles, userTypes} = getCurrentPathAccess();
        if(userRoles && !userTypes.includes(website?.user_type) && !userRoles.includes(selectedWebsite?.role)) {
            history.push('/dashboard');
        }
    }

    const deleteWebsite = (_id) => {
        const updatedWebsites = websites.filter(({id}) => id !== _id);
        setWebsites(updatedWebsites);
    }

   const {refetch: refetchProfile} = useQuery('fetchUserDetails',  getProfile , {
        enabled: isLoggedIn && !userDetails,
        onSuccess: (userDetails) => {
            setUserDetails(userDetails.user);
            setPlanDetails(userDetails.subscription);
            setIsShopify(!!userDetails?.owner?.has_shopify);
            setIsPromotionalUser(!!userDetails?.owner?.has_coupon);
        }
    });

    useQuery('Shopify Billing', getSubscriptionById, {
        enabled: isLoggedIn && isShopify && !!planDetails,
        onSuccess: (data) => {
            if(data.errors) {
                setShopifyFreeUser(true);
                // toast.error('Something went wrong!')
            } else {
                const endDate = moment(data.next_billing_date).subtract(1, 'day');
                const planDetail = {...planDetails, status: data.status, current_period_end: endDate};
                setPlanDetails(planDetail);
            }
        }
    })

    const {refetch: refetchWebsites} = useQuery('fetchWebsites', getWebSites, {
        enabled: isLoggedIn,
        onSuccess: ({ websites, default: defaultWebsite }) => {
            setWebsites(websites);
            setDefaultWebsite(defaultWebsite);
            const previousSelected = localStorage.getItem('selected_website');
            if(previousSelected) {
                const selectedWebsite = websites.find(({id}) => Number(previousSelected) === id);
                if(selectedWebsite) {
                    getSelectedWebsite(selectedWebsite?.id);
                    
                } else {
                    setSelectedWebsite(defaultWebsite);
                }

            } else {
                setSelectedWebsite(defaultWebsite);
            }
        }, 
        onError: () => {}
    });

    const {refetch: refetchFeatures} = useQuery(['fetchFeatures', selectedWebsite?.id], 
        () => getLockedFeatures(selectedWebsite?.id),
        {
            enabled: !!selectedWebsite,
            onSuccess: (data) => {
                if(data.errors) {

                } else {
                    const featureKeywords = data.map(({keyword}) => keyword);
                    setAvailableFeatures(featureKeywords);
                }
            }
        }
    );

    const {refetch: refetchCardDetails} = useQuery('fetchCard', getCardDetails,
        {
            enabled: isLoggedIn,
            onSuccess: (data) => {
                if(data.errors) {

                } else {
                    if(!data.last4) {
                        setCardDetails(null);
                    } else {
                        setCardDetails(data);
                    }
                }
            }
        }
    );


    const getSelectedWebsite = (websiteId) => {
        fetchSelectedWebsite.mutate(websiteId);
    }

    const fetchSelectedWebsite = useMutation((websiteId) => getWebSite(websiteId),{
        onSuccess: (websiteData) => {
            if(websiteData.errors) {
                toast.error('Our server isn\'t able to fetch the data for selected website at the moment, Navigating you to default website');
            } else {
                setSelectedWebsite(websiteData);
            }
        }, 
        onError: () => {
            toast.error('Our server isn\'t able to fetch the data for selected website at the moment, Navigating you to default website');
        }
    })

    const isFeatureAvailable = (feature) => {
        if(feature === 'woocommerce') {
            if(selectedWebsite && !selectedWebsite?.has_woo_commerce) {
                history.push('/dashboard');
            }
            else if(selectedWebsite?.suspension_reason.length > 0) {
                return false;
            }
            return true;
        }
        else if(selectedWebsite?.suspension_reason.length > 0) {
            return false;
        }
        else {
            if(feature === 'shopify' && selectedWebsite && !selectedWebsite?.has_shopify) {
                history.push('/dashboard');
            }
            return availableFeatures.includes(feature);
        }  
        
    }

    const setIsLoadings = (value, url) => {
        setIsLoading(value);
        setChangePlanPopupOpen(false);
        window.open(url, '_parent');
    }

    const resetAllStates = () => {
        setWebsites([]);
        setSelectedWebsite(null);
        setUserDetails(null);
        setMenuOpen(false);
        setPlanDetails(null);
        setIsDirty(false);
        setMenuCollapsed(false); 
    }

    return <AppContext.Provider value = {{
        isLoggedIn,
        websites,
        isDirty,
        selectedWebsite,
        userDetails,
        isFetching: isFetching - isFetchingGrowth,
        isMutating,
        isMenuOpen,
        planDetails,
        isMenuCollapsed,
        isBgDisabled,
        discardCallBack, 
        isDiscardPromptOpen,
        pathToNavigate, 
        isPricingPopupOpen,
        isChangePlanPopupOpen,
        chosenPlan,
        isCancelPlanPopupOpen,
        isVerifyPopupOpen,
        isPaymentSuccess,
        isCardSuccess, 
        setCardSuccess,
        isPaymentFailure, 
        setPaymentFailure,
        isCardFailure, 
        setCardFailure,
        isUpdatePlanSuccess, 
        setUpdatePlanSuccess,
        isUpdatePlanFailure, 
        setUpdatePlanFailure,
        isCancelPlanSuccess, 
        setCancelPlanSuccess,
        isCancelPlanFailure, 
        setCancelPlanFailure,
        setPaymentSuccess,
        setIsVerifyPopupOpen,
        isFeatureAvailable,
        setCancelPlanPopupOpen, 
        setChosenPlan,
        setChangePlanPopupOpen,
        setPricingPopupOpen,
        setPathToNavigate,
        setDiscardPromptOpen,
        setDiscardCallBack,
        setIsDirty,
        addWebsite,
        updateWebsites,
        deleteWebsite,
        setSelectedWebsite,
        setUserDetails,
        setMenuOpen,
        refetchWebsites,
        setMenuCollapsed,
        setPlanDetails,
        resetAllStates,
        setIsBgDisabled,
        refetchProfile,
        refetchFeatures,
        cardDetails, 
        setCardDetails,
        refetchCardDetails,
        chatVisibility, 
        setChatVisibility,
        isShopify,
        setIsShopify,
        isLoading,
        setIsLoadings,
        isPromotionalUser,
        isShopifyFreeUser,
        getSelectedWebsite,
        defaultWebsite
        }}>
            {children}
    </AppContext.Provider>
}

const useAppContext = () => useContext(AppContext);

export { AppContextProvider, useAppContext};